import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  useConvertEstimate,
  useEstimate,
  useExtendValidity,
  useMarkAccepted,
  useMarkDeclined,
  useSendEstimate,
  useVoidEstimate
} from './hooks';
import { isExpired } from './isExpired';
import { useVisitList } from '../visits/hooks';
import { usePageTitle } from '../../hooks/usePageTitle';
import { getApiErrorMessage } from '../../api/errorMessage';

export function EstimateDetailPage() {
  usePageTitle('Estimate');
  const { id = '' } = useParams();
  const nav = useNavigate();
  const { data: estimate, isLoading } = useEstimate(id);

  const send = useSendEstimate();
  const extend = useExtendValidity();
  const accept = useMarkAccepted();
  const decline = useMarkDeclined();
  const convert = useConvertEstimate();
  const voidEstimate = useVoidEstimate();

  const [actionError, setActionError] = useState<string | null>(null);
  const [showConvert, setShowConvert] = useState(false);
  const [convertVisitId, setConvertVisitId] = useState('');
  const [extendDays, setExtendDays] = useState('');
  const [showExtended, setShowExtended] = useState(false);

  // Visit picker is filtered to the estimate's customer (D9). Loaded lazily.
  const { data: visits } = useVisitList(
    estimate && showConvert ? { customerId: estimate.customerId } : {}
  );

  if (isLoading) return <div className="max-w-3xl mx-auto px-4 py-8 text-slate-500">Loading…</div>;
  if (!estimate)
    return (
      <div className="max-w-3xl mx-auto px-4 py-8 text-slate-500">
        Estimate not found.{' '}
        <Link to="/estimates" className="text-sky-600">Back to list</Link>
      </div>
    );

  const expired = isExpired(estimate.status, estimate.expiresAtUtc);
  const canSend = estimate.status === 'Draft' || estimate.status === 'Sent';
  const canExtend = estimate.status === 'Sent';
  const canDecide = estimate.status === 'Sent';
  const canConvert = estimate.status === 'Sent' || estimate.status === 'Accepted';
  const canVoid = estimate.status !== 'Converted' && estimate.status !== 'Void';

  const onSend = async () => {
    if (!confirm(`Send estimate ${estimate.estimateNumber} to the customer?`)) return;
    setActionError(null);
    try {
      await send.mutateAsync(estimate.id);
    } catch (e) {
      setActionError(getApiErrorMessage(e, "Couldn't send the estimate — please try again."));
    }
  };

  const onExtend = async () => {
    setActionError(null);
    setShowExtended(false);
    try {
      const days = extendDays.trim() === '' ? undefined : Number(extendDays);
      await extend.mutateAsync({ id: estimate.id, days });
      setExtendDays('');
      setShowExtended(true); // hint the sitter to re-send afterward
    } catch (e) {
      setActionError(getApiErrorMessage(e, "Couldn't extend validity — please try again."));
    }
  };

  const onAccept = async () => {
    setActionError(null);
    try {
      await accept.mutateAsync(estimate.id);
    } catch (e) {
      setActionError(getApiErrorMessage(e, "Couldn't mark accepted — please try again."));
    }
  };

  const onDecline = async () => {
    const reason = prompt('Decline reason (optional):');
    if (reason === null) return;
    setActionError(null);
    try {
      await decline.mutateAsync({ id: estimate.id, notes: reason || undefined });
    } catch (e) {
      setActionError(getApiErrorMessage(e, "Couldn't mark declined — please try again."));
    }
  };

  const onConvert = async () => {
    setActionError(null);
    try {
      const { result } = await convert.mutateAsync({
        id: estimate.id,
        visitId: convertVisitId || null
      });
      // Land the sitter on the editable Draft receipt with everything pre-filled (D9).
      nav(`/receipts/${result.receiptId}`);
    } catch (e) {
      setActionError(getApiErrorMessage(e, "Couldn't convert the estimate — please try again."));
    }
  };

  const onVoid = async () => {
    const reason = prompt('Void reason (optional):');
    if (reason === null) return;
    setActionError(null);
    try {
      await voidEstimate.mutateAsync({ id: estimate.id, notes: reason || undefined });
    } catch (e) {
      setActionError(getApiErrorMessage(e, "Couldn't void the estimate — please try again."));
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 py-8 space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <Link to="/estimates" className="text-sm text-slate-500 hover:text-sky-600">
            ← Estimates
          </Link>
          <h1 className="text-2xl font-semibold text-slate-800 mt-2 font-mono">
            {estimate.estimateNumber}
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            {estimate.customerName} • {new Date(estimate.issuedAtUtc).toLocaleDateString()}
            {estimate.expiresAtUtc && <> • valid until {new Date(estimate.expiresAtUtc).toLocaleDateString()}</>}
          </p>
        </div>
        <StatusBadge status={estimate.status} expired={expired} />
      </div>

      <section className="bg-white border border-slate-200 rounded-xl p-6 space-y-3">
        <table className="w-full text-sm">
          <thead className="text-slate-600">
            <tr>
              <th className="text-left font-medium pb-2">Description</th>
              <th className="text-right font-medium pb-2">Amount</th>
            </tr>
          </thead>
          <tbody>
            {estimate.lineItems.map((li) => (
              <tr key={li.id} className="border-t border-slate-100">
                <td className="py-2">{li.description}</td>
                <td className="py-2 text-right">{li.lineTotal.toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="text-right text-sm space-y-1 pt-2 border-t border-slate-100">
          <div>Subtotal: {estimate.subtotal.toFixed(2)} {estimate.currency}</div>
          {estimate.adjustments !== 0 && (
            <div>Adjustments: {estimate.adjustments.toFixed(2)} {estimate.currency}</div>
          )}
          <div className="text-lg font-semibold text-slate-800">
            Total: {estimate.total.toFixed(2)} {estimate.currency}
          </div>
        </div>
        {estimate.notes && (
          <div className="text-sm text-slate-600 border-t border-slate-100 pt-3 whitespace-pre-wrap">
            <strong className="text-slate-700">Notes: </strong>
            {estimate.notes}
          </div>
        )}
      </section>

      {estimate.status === 'Converted' && estimate.convertedToReceiptId && (
        <div className="text-sm text-indigo-800 bg-indigo-50 border border-indigo-200 rounded p-3">
          Converted to a receipt.{' '}
          <Link to={`/receipts/${estimate.convertedToReceiptId}`} className="underline">
            Open the receipt
          </Link>
          .
        </div>
      )}

      <section className="bg-white border border-slate-200 rounded-xl p-6 space-y-3">
        <h2 className="text-sm font-semibold text-slate-700">Actions</h2>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            disabled={!canSend || send.isPending}
            onClick={onSend}
            className="bg-sky-600 text-white px-4 py-2 rounded hover:bg-sky-700 disabled:opacity-50 text-sm"
          >
            {send.isPending ? 'Sending…' : estimate.status === 'Sent' ? 'Resend' : 'Send estimate'}
          </button>
          <button
            type="button"
            disabled={!canDecide || accept.isPending}
            onClick={onAccept}
            className="border border-slate-300 text-slate-700 px-4 py-2 rounded hover:bg-slate-50 disabled:opacity-50 text-sm"
          >
            Mark accepted
          </button>
          <button
            type="button"
            disabled={!canDecide || decline.isPending}
            onClick={onDecline}
            className="border border-slate-300 text-slate-700 px-4 py-2 rounded hover:bg-slate-50 disabled:opacity-50 text-sm"
          >
            Mark declined
          </button>
          <button
            type="button"
            disabled={!canConvert || convert.isPending}
            onClick={() => {
              setConvertVisitId('');
              setActionError(null);
              setShowConvert(true);
            }}
            className="border border-slate-300 text-slate-700 px-4 py-2 rounded hover:bg-slate-50 disabled:opacity-50 text-sm"
          >
            Convert to Bill
          </button>
          <button
            type="button"
            disabled={!canVoid || voidEstimate.isPending}
            onClick={onVoid}
            className="border border-slate-300 text-slate-700 px-4 py-2 rounded hover:bg-slate-50 disabled:opacity-50 text-sm"
          >
            Void
          </button>
        </div>

        {canExtend && (
          <div className={`rounded p-3 border ${expired ? 'border-amber-300 bg-amber-50' : 'border-slate-200 bg-slate-50'}`}>
            <div className="text-xs font-medium text-slate-700 mb-1">
              Extend validity
              {expired && <span className="ml-1 text-amber-700">— this estimate has expired</span>}
            </div>
            <div className="flex items-center gap-2">
              <input
                type="number"
                min="1"
                max="365"
                value={extendDays}
                onChange={(e) => setExtendDays(e.target.value)}
                placeholder="days (default tenant setting)"
                className="w-48 border border-slate-300 rounded px-2 py-1.5 text-sm"
                aria-label="Extend validity days"
              />
              <button
                type="button"
                onClick={onExtend}
                disabled={extend.isPending}
                className="bg-slate-700 text-white px-3 py-1.5 rounded hover:bg-slate-800 disabled:opacity-50 text-sm"
              >
                Extend
              </button>
            </div>
            {showExtended && (
              <p className="text-xs text-slate-600 mt-2">
                Validity updated. Re-send the estimate so the customer gets the refreshed PDF and a fresh link.
              </p>
            )}
          </div>
        )}

        {actionError && (
          <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded p-2">
            {actionError}
          </div>
        )}

        {send.isSuccess && (
          <div className="text-xs text-green-700 bg-green-50 border border-green-200 rounded p-2">
            Sent — email outbox id{' '}
            <span className="font-mono">{send.data?.result.emailOutboxId.slice(0, 8)}</span>.
          </div>
        )}
      </section>

      {showConvert && (
        <section className="bg-white border border-slate-200 rounded-xl p-6 space-y-4">
          <h2 className="text-sm font-semibold text-slate-700">Convert to bill</h2>
          <p className="text-xs text-slate-500">
            Creates a draft receipt with these line items pre-filled. You can optionally link a visit.
          </p>
          <label className="block">
            <span className="block text-sm font-medium text-slate-700 mb-1">Visit (optional)</span>
            <select
              value={convertVisitId}
              onChange={(e) => setConvertVisitId(e.target.value)}
              className="w-full border border-slate-300 rounded px-3 py-2 min-h-[44px]"
            >
              <option value="">No visit</option>
              {visits?.map((v) => (
                <option key={v.id} value={v.id}>
                  {new Date(v.startUtc).toLocaleDateString()} — {v.status}
                </option>
              ))}
            </select>
          </label>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onConvert}
              disabled={convert.isPending}
              className="bg-sky-600 text-white px-4 py-2 rounded hover:bg-sky-700 disabled:opacity-50 text-sm"
            >
              {convert.isPending ? 'Converting…' : 'Create receipt'}
            </button>
            <button
              type="button"
              onClick={() => setShowConvert(false)}
              className="text-slate-600 hover:text-sky-600 text-sm"
            >
              Cancel
            </button>
          </div>
        </section>
      )}
    </div>
  );
}

function StatusBadge({ status, expired }: { status: string; expired: boolean }) {
  if (expired) {
    return (
      <span className="inline-flex items-center text-xs px-3 py-1 rounded border bg-amber-50 text-amber-800 border-amber-200">
        Expired
      </span>
    );
  }
  const palette: Record<string, string> = {
    Draft: 'bg-slate-100 text-slate-700 border-slate-200',
    Sent: 'bg-sky-50 text-sky-800 border-sky-200',
    Accepted: 'bg-green-50 text-green-800 border-green-200',
    Declined: 'bg-red-50 text-red-800 border-red-200',
    Converted: 'bg-indigo-50 text-indigo-800 border-indigo-200',
    Void: 'bg-red-50 text-red-800 border-red-200'
  };
  return (
    <span className={`inline-flex items-center text-xs px-3 py-1 rounded border ${palette[status] ?? ''}`}>
      {status}
    </span>
  );
}
